import dayjs from 'dayjs';

const EMPTY_VARIANCE_TOTALS = {
  creditCardCurrent: 0,
  creditCardPosted: 0,
  creditVariance: 0,
  cashCurrent: 0,
  cashPosted: 0,
  cashVariance: 0,
  totalCurrent: 0,
  totalVariance: 0,
};

const toNumber = (value) => {
  if (value == null || value === '') return 0;

  const numberValue = typeof value === 'number' ? value : Number(value);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatVarianceDateAffected = (startDate, endDate) => {
  if (!startDate || !endDate) return '—';

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) return '—';

  return `${start.format('MMM D')}–${end.format('MMM D')}`;
};

const mapVarianceRow = (item) => ({
  sublocation: item?.sublocationName || (item?.isBalancingEntry ? 'Balancing Entry' : '—'),
  dateAffected: formatVarianceDateAffected(item?.startDate, item?.endDate),
  monthlyPostingId: item?.monthlyPostingId || item?.glMonthlyPostingId || '—',
  assetId: item?.assetId || '—',
  // Support both formats: creditCurrent (Open tab) and creditCardCurrent (Finalized API)
  creditCardCurrent: toNumber(item?.creditCurrent ?? item?.creditCardCurrent),
  creditCardPosted: toNumber(item?.creditPosted ?? item?.creditCardPosted),
  creditVariance: toNumber(item?.creditVariance),
  cashCurrent: toNumber(item?.cashCurrent),
  cashPosted: toNumber(item?.cashPosted),
  cashVariance: toNumber(item?.cashVariance),
  totalCurrent: toNumber(item?.totalCurrent),
  totalVariance: toNumber(item?.totalVariance),
  isBalancingEntry: item?.isBalancingEntry || false,
});

const accumulateVarianceTotals = (totals, row) => ({
  creditCardCurrent: totals.creditCardCurrent + row.creditCardCurrent,
  creditCardPosted: totals.creditCardPosted + row.creditCardPosted,
  creditVariance: totals.creditVariance + row.creditVariance,
  cashCurrent: totals.cashCurrent + row.cashCurrent,
  cashPosted: totals.cashPosted + row.cashPosted,
  cashVariance: totals.cashVariance + row.cashVariance,
  totalCurrent: totals.totalCurrent + row.totalCurrent,
  totalVariance: totals.totalVariance + row.totalVariance,
});

const buildVarianceSummaryRow = (totals) => ({
  sublocation: 'TOTAL',
  dateAffected: '',
  monthlyPostingId: '',
  assetId: '',
  creditCardCurrent: totals.creditCardCurrent,
  creditCardPosted: totals.creditCardPosted,
  creditVariance: totals.creditVariance,
  cashCurrent: totals.cashCurrent,
  cashPosted: totals.cashPosted,
  cashVariance: totals.cashVariance,
  totalCurrent: totals.totalCurrent,
  totalVariance: totals.totalVariance,
  isSummaryRow: true,
});

export const buildVarianceTableData = (assetWithVarianceData) => {
  const rows = (Array.isArray(assetWithVarianceData) ? assetWithVarianceData : []).map(mapVarianceRow);
  if (rows.length === 0) return [];

  const totals = rows.reduce(accumulateVarianceTotals, EMPTY_VARIANCE_TOTALS);
  return [...rows, buildVarianceSummaryRow(totals)];
};

const buildMonthlySummaryRow = (calculations) => {
  const totalVariance = calculations.totalVariance ?? 0;
  const totalRefundCurrent = calculations.totalRefundCurrent ?? 0;

  return {
    weekLabel: 'TOTAL',
    postingId: 'TOTAL',
    productType: '',
    creditCardCurrent: calculations.totalCurrentCredit,
    creditCardPosted: calculations.totalPostedCredit,
    creditVariance: calculations.creditVariance,
    cashCurrent: calculations.totalCurrentCash,
    cashPosted: calculations.totalPostedCash,
    cashVariance: calculations.cashVariance,
    refundCurrent: totalRefundCurrent,
    totalCurrent: calculations.totalCurrentCredit + calculations.totalCurrentCash - totalVariance - totalRefundCurrent,
    totalVariance,
    isSummaryRow: true,
    summaryType: 'total',
  };
};

const mapWeeklyPostingRow = (row, selectedProductName) => ({
  ...row,
  productType: selectedProductName || row?.x3ProductName || '—',
  isIncluded: row?.sublocations ? `${row.sublocations.selected?.length || 0}/${row.sublocations.total || 0}` : '—',
});

export const buildMonthlyPostingTableData = (weeklyPostingsData, selectedProductName, calculations) => {
  const regularRows = (Array.isArray(weeklyPostingsData) ? weeklyPostingsData : []).map((row) =>
    mapWeeklyPostingRow(row, selectedProductName),
  );

  if (regularRows.length === 0) return [];

  return [...regularRows, buildMonthlySummaryRow(calculations)];
};
